import { useQuery } from '@apollo/client';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import * as styles from './CommentHistory.module.scss';
import HistoricalCommentThread from './HistoricalCommentThread';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import EmptyState from '../../components/EmptyState';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import groupByCutoffs from '../../utils/groupByCutoffs';
import useNavigate from '../../utils/router/useNavigate';
import commentThreadHash, {
  getThreadIdFromUrlHash,
} from '../../utils/url/commentThreadHash';
import postPath from '../../utils/url/postPath';
import historyCutoffs from '../history/historyCutoffs';
import useUnresolveThread from '../utils/useUnresolveThread';

const POST_FRAGMENT = gql(`
  fragment CommentHistory_post on Post {
    id
    ...postPath_post
  }
`);

const HISTORICAL_THREADS_QUERY = gql(/** @complexity 25 */ `
  query historicalThreads($postId: ID!) {
    post(id: $postId) {
      id
      threads(historical: true) {
        id
        ...HistoricalCommentThread_thread
      }
    }
  }
`);

interface CommentHistorySidebarProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const CommentHistory = ({ post }: CommentHistorySidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { id: postId } = post;
  const { loading, data } = useQuery(HISTORICAL_THREADS_QUERY, {
    fetchPolicy: 'network-only',
    variables: { postId },
  });

  const threads = data?.post ? data.post.threads : [];

  const targetRef = useRef<HTMLDivElement | null>(null);
  const targetThreadIdRef = useRef(getThreadIdFromUrlHash(location.hash));

  useEffect(() => {
    if (!targetThreadIdRef.current || loading) return;

    if (targetRef.current) {
      targetRef.current.scrollIntoView();
    } else {
      dispatch(
        UIActions.pushToast({
          level: 'error',
          message: 'That comment thread no longer exists.',
        }),
      );
    }
  }, [dispatch, loading]);

  const unresolveThread = useUnresolveThread();

  if (!loading && threads.length === 0) {
    return (
      <EmptyState
        title="No comment history"
        message="All resolved and deleted comments will appear here."
        vertical
      />
    );
  }

  const cutoffs = historyCutoffs();
  return (
    <>
      {groupByCutoffs(cutoffs, threads, 'resolvedAt').map(
        ({ items, key, label }) => (
          <div className={styles.threadWrapper} key={key}>
            {label && <div className={styles.sectionHeader}>{label}</div>}
            {items.map((item) => (
              <div
                key={item.id}
                ref={
                  item.id === targetThreadIdRef.current ? targetRef : undefined
                }
              >
                <HistoricalCommentThread
                  thread={item}
                  onUnresolve={() =>
                    unresolveThread(item).then(() => {
                      const url = `${postPath(post)}${commentThreadHash(
                        item.id,
                      )}`;
                      navigate(url);
                    })
                  }
                />
              </div>
            ))}
          </div>
        ),
      )}
    </>
  );
};

export default CommentHistory;
